import { useState } from "react";
import axios from "axios";
import { API } from "../../config";
import { useLanguage } from "../../contexts/LanguageContext";
import {
  Search, Loader2, TrendingUp, ArrowUpRight, Target,
  BarChart3, Sparkles, ChevronDown, ChevronUp, FileText
} from "lucide-react";

const INTENT_COLORS = {
  informational: "bg-blue-900/30 text-blue-400",
  commercial: "bg-emerald-900/30 text-emerald-400",
  transactional: "bg-amber-900/30 text-amber-400",
  navigational: "bg-purple-900/30 text-purple-400",
};

export default function KeywordResearchTab({ user }) {
  const { t } = useLanguage();
  const [url, setUrl] = useState("");
  const [niche, setNiche] = useState("");
  const [loading, setLoading] = useState(false);
  const [keywords, setKeywords] = useState(null);
  const [error, setError] = useState("");
  const [sortBy, setSortBy] = useState("opportunity_score");
  const [sortDir, setSortDir] = useState("desc");

  const handleResearch = async () => {
    if (!url.trim() && !niche.trim()) return;
    setLoading(true);
    setError("");
    setKeywords(null);
    try {
      const res = await axios.post(`${API}/keyword-research/competitor`, {
        url: url.trim(),
        niche: niche.trim(),
        language: "en",
        email: user?.email || "",
      });
      setKeywords(res.data.keywords || []);
    } catch (e) {
      setError(e.response?.data?.detail || "Error occurred");
    } finally {
      setLoading(false);
    }
  };

  const sorted = keywords
    ? [...keywords].sort((a, b) => {
        const av = a[sortBy] || 0;
        const bv = b[sortBy] || 0;
        return sortDir === "desc" ? bv - av : av - bv;
      })
    : null;

  const toggleSort = (col) => {
    if (sortBy === col) setSortDir(d => d === "desc" ? "asc" : "desc");
    else { setSortBy(col); setSortDir("desc"); }
  };

  const SortIcon = ({ col }) => {
    if (sortBy !== col) return <ChevronDown className="w-3 h-3 text-zinc-600" />;
    return sortDir === "desc"
      ? <ChevronDown className="w-3 h-3 text-[#FFE600]" />
      : <ChevronUp className="w-3 h-3 text-[#FFE600]" />;
  };

  const getDifficultyColor = (d) => {
    if (d <= 20) return "text-emerald-400";
    if (d <= 40) return "text-green-400";
    if (d <= 60) return "text-amber-400";
    if (d <= 80) return "text-orange-400";
    return "text-red-400";
  };

  const getScoreColor = (s) => {
    if (s >= 80) return "text-emerald-400";
    if (s >= 60) return "text-green-400";
    if (s >= 40) return "text-amber-400";
    return "text-red-400";
  };

  return (
    <div className="space-y-6" data-testid="keyword-research-tab">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-black font-['Cabinet_Grotesk'] tracking-tight" data-testid="keyword-research-title">
          {t.kw_title || "Keyword Research"}
        </h2>
        <p className="text-zinc-500 text-sm mt-1">
          {t.kw_desc || "AI analyzes your competitors and finds high-value keyword opportunities"}
        </p>
      </div>

      {/* Input Form */}
      <div className="bg-[#141414] border border-zinc-800 p-6 space-y-4" data-testid="keyword-research-form">
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="text-xs text-zinc-400 font-['JetBrains_Mono'] uppercase tracking-wider mb-1 block">
              {t.kw_competitor_url || "Competitor URL"}
            </label>
            <input
              type="text"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://competitor.com"
              className="w-full px-4 py-3 bg-[#0a0a0a] border border-zinc-700 text-white focus:border-[#a855f7] focus:outline-none font-['JetBrains_Mono'] text-sm"
              data-testid="keyword-url-input"
            />
          </div>
          <div>
            <label className="text-xs text-zinc-400 font-['JetBrains_Mono'] uppercase tracking-wider mb-1 block">
              {t.kw_niche || "Business Niche"}
            </label>
            <input
              type="text"
              value={niche}
              onChange={(e) => setNiche(e.target.value)}
              placeholder={t.kw_niche_placeholder || "e.g. dental clinic Prague"}
              className="w-full px-4 py-3 bg-[#0a0a0a] border border-zinc-700 text-white focus:border-[#a855f7] focus:outline-none font-['JetBrains_Mono'] text-sm"
              data-testid="keyword-niche-input"
            />
          </div>
        </div>
        <button
          onClick={handleResearch}
          disabled={loading || (!url.trim() && !niche.trim())}
          className="px-6 py-3 bg-[#a855f7] text-white font-bold uppercase tracking-wider hover:bg-purple-600 transition-colors disabled:opacity-50 flex items-center gap-2 text-sm"
          data-testid="keyword-research-btn"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
          {loading ? (t.kw_analyzing || "Analyzing...") : (t.kw_find || "Find Keywords")}
        </button>
        {error && <p className="text-red-400 text-sm">{error}</p>}
      </div>

      {/* Results */}
      {sorted && (
        <div className="bg-[#141414] border border-zinc-800 overflow-hidden" data-testid="keyword-results">
          {/* Stats bar */}
          <div className="flex items-center gap-6 p-4 border-b border-zinc-800 bg-[#0a0a0a]">
            <div className="flex items-center gap-2">
              <Target className="w-4 h-4 text-[#a855f7]" />
              <span className="text-sm text-zinc-300">{sorted.length} {t.kw_keywords_found || "keywords found"}</span>
            </div>
            <div className="flex items-center gap-2">
              <BarChart3 className="w-4 h-4 text-emerald-400" />
              <span className="text-sm text-zinc-300">
                {sorted.reduce((s, k) => s + (k.volume || 0), 0).toLocaleString()} {t.kw_total_volume || "total monthly volume"}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-[#FFE600]" />
              <span className="text-sm text-zinc-300">
                {sorted.filter(k => (k.opportunity_score || 0) >= 80).length} {t.kw_high_opp || "high opportunity"}
              </span>
            </div>
          </div>

          {/* Table */}
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="keyword-table">
              <thead>
                <tr className="border-b border-zinc-800 text-xs text-zinc-500 font-['JetBrains_Mono'] uppercase">
                  <th className="text-left px-4 py-3">{t.kw_col_keyword || "Keyword"}</th>
                  <th className="px-4 py-3 cursor-pointer select-none" onClick={() => toggleSort("volume")}>
                    <span className="flex items-center justify-center gap-1">{t.kw_col_volume || "Volume"} <SortIcon col="volume" /></span>
                  </th>
                  <th className="px-4 py-3 cursor-pointer select-none" onClick={() => toggleSort("difficulty")}>
                    <span className="flex items-center justify-center gap-1">{t.kw_col_kd || "KD"} <SortIcon col="difficulty" /></span>
                  </th>
                  <th className="px-4 py-3">{t.kw_col_intent || "Intent"}</th>
                  <th className="px-4 py-3 cursor-pointer select-none" onClick={() => toggleSort("opportunity_score")}>
                    <span className="flex items-center justify-center gap-1">{t.kw_col_score || "Score"} <SortIcon col="opportunity_score" /></span>
                  </th>
                  <th className="text-left px-4 py-3">{t.kw_col_title || "Suggested Title"}</th>
                </tr>
              </thead>
              <tbody>
                {sorted.map((kw, i) => (
                  <tr key={i} className="border-b border-zinc-800/50 hover:bg-zinc-900/50 transition-colors" data-testid={`keyword-row-${i}`}>
                    <td className="px-4 py-3">
                      <span className="text-white text-sm font-medium">{kw.keyword}</span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className="text-zinc-300 text-sm font-['JetBrains_Mono']">
                        {(kw.volume || 0).toLocaleString()}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className={`text-sm font-bold font-['JetBrains_Mono'] ${getDifficultyColor(kw.difficulty)}`}>
                        {kw.difficulty}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className={`text-[10px] font-bold uppercase px-2 py-0.5 ${INTENT_COLORS[kw.intent] || "bg-zinc-800 text-zinc-400"}`}>
                        {kw.intent}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className={`text-lg font-black font-['Cabinet_Grotesk'] ${getScoreColor(kw.opportunity_score)}`}>
                        {kw.opportunity_score}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-zinc-400 text-xs">{kw.suggested_title}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Empty state */}
      {!keywords && !loading && (
        <div className="bg-[#141414] border border-zinc-800 p-12 text-center">
          <Search className="w-12 h-12 text-zinc-700 mx-auto mb-4" />
          <h3 className="text-white font-bold font-['Cabinet_Grotesk'] text-lg mb-2">
            {t.kw_empty_h || "Find your best keywords"}
          </h3>
          <p className="text-zinc-500 text-sm max-w-md mx-auto">
            {t.kw_empty_desc || "Enter a competitor URL or your business niche and our AI will find high-value keyword opportunities you can rank for."}
          </p>
        </div>
      )}
    </div>
  );
}
